import React from 'react';
import { Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { PublicCourse } from '../services/publicService';
import { formatPrice } from '../lib/format';
import { hasMeaningfulStudentCount, showsFacilitator } from '../lib/courseMeta';
import { THUMB_PLACEHOLDER, onThumbError } from './ui/imageFallback';
import { thumbnailSrc } from '../lib/imageUrl';

interface PublicCourseCardProps {
  course: PublicCourse;
  /** Suppress the category badge when it's uniform across the rendered list (see lib/cardBadges). */
  hideCategory?: boolean;
  /** Suppress the level badge when it's uniform across the rendered list. */
  hideLevel?: boolean;
}

export const PublicCourseCard: React.FC<PublicCourseCardProps> = ({ course, hideCategory, hideLevel }) => {
  const navigate = useNavigate();
  const showStudentCount = hasMeaningfulStudentCount(course.totalStudents);
  const showFacilitator = showsFacilitator(course);
  return (
    <Link to={`/course/${course.id}`} className="group h-full">
      <div className="bg-white rounded-2xl overflow-hidden hover:shadow-card-hover shadow-card transition-all duration-300 flex flex-col h-full transform hover:-translate-y-1 ring-1 ring-gray-100">
        {/* Image Container */}
        <div className="relative aspect-[16/9] bg-gray-100 overflow-hidden">
          <img
            src={thumbnailSrc(course.thumbnailUrl) || THUMB_PLACEHOLDER}
            onError={onThumbError}
            alt={course.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60"></div>
          {!hideLevel && (
            <div className="absolute top-3 right-3 bg-white/95 backdrop-blur px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider text-gray-900 shadow-sm">
              {course.level}
            </div>
          )}
          {!hideCategory && (
            <div className="absolute bottom-3 left-3 text-white font-medium text-xs flex items-center gap-2">
              <span className="bg-primary-600 px-2 py-0.5 rounded text-white font-semibold shadow-sm">
                {course.category}
              </span>
            </div>
          )}
        </div>

        <div className="p-6 flex flex-col flex-1">
          <h3 className="font-sans text-xl font-bold text-gray-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors tracking-tight">
            {course.title}
          </h3>
          <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed flex-1">
            {course.description}
          </p>

          {course.tags && course.tags.length > 0 && (
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-4 text-[10px] uppercase tracking-wider">
              {course.tags.slice(0, 3).map((tag, i) => (
                <React.Fragment key={tag.id}>
                  {i > 0 && <span className="text-gray-300" aria-hidden>·</span>}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/?tag=${tag.id}`);
                    }}
                    className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-900 transition-colors font-semibold"
                  >
                    <span
                      aria-hidden
                      className="h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color || '#9ca3af' }}
                    />
                    {tag.name}
                  </button>
                </React.Fragment>
              ))}
              {course.tags.length > 3 && (
                <span className="text-gray-400 font-semibold">+{course.tags.length - 3}</span>
              )}
            </div>
          )}

          {/* Meta row. There is deliberately no rating here: nothing on the
              platform ever writes courses.rating — no reviews table, no
              trigger — so it renders "★ 0.0" on every course forever. The
              signed-in CourseCard dropped it long ago; this card was the last
              surface still showing it, to the audience least able to read it
              as an artefact. */}
          {showStudentCount && (
            <div className="flex items-center gap-4 text-xs font-semibold text-gray-400 mb-5 border-b border-gray-100 pb-5">
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                <span>{course.totalStudents.toLocaleString()} students</span>
              </div>
            </div>
          )}

          <div className={`flex items-center ${showFacilitator ? 'justify-between' : 'justify-end'}`}>
            {showFacilitator && (
              <div className="flex flex-col">
                <span className="text-[10px] uppercase text-gray-400 font-bold tracking-wider mb-0.5">
                  Instructor
                </span>
                <span className="text-xs font-bold text-gray-800">
                  {course.instructorName}
                </span>
              </div>
            )}
            <div className="text-right">
              <span className="block text-lg font-bold text-primary-700">
                {course.price === 0 ? 'Free' : formatPrice(course.price)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
